/* eslint-disable jsx-a11y/anchor-is-valid */

import {
  Bars3Icon,
  MoonIcon,
  QuestionMarkCircleIcon,
  SunIcon,
} from '@heroicons/react/24/outline'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { useTheme } from 'next-themes'
import { IconButton, Logo, NavItem } from 'pcl-ui'
import { type MouseEvent, useState } from 'react'
import { track } from 'track'
import { articles } from '@/state/articles'
import i18nextConfig from '../next-i18next.config'
import { Drawer } from './Drawer'

const OnboardingModal = dynamic(() => import('./OnboardingModal'), {
  ssr: false,
})

function ThemeChanger() {
  const { theme, setTheme, resolvedTheme } = useTheme()
  const { t } = useTranslation()

  const handleClick = () => {
    if (theme === 'system') {
      const newTheme = resolvedTheme === 'dark' ? 'light' : 'dark'
      setTheme(newTheme)
      track('event', `click-theme-change-${newTheme}`)
    } else {
      const newTheme = theme === 'dark' ? 'light' : 'dark'
      setTheme(newTheme)
      track('event', `click-theme-change-${newTheme}`)
    }
  }

  return (
    <IconButton
      label={t('Header.toggleTheme')}
      onClick={handleClick}
      data-test="theme-change-button"
    >
      <MoonIcon className="h-5 w-5 block dark:hidden" aria-hidden="true" />
      <SunIcon className="h-5 w-5 hidden dark:block" aria-hidden="true" />
    </IconButton>
  )
}

interface TopHeaderProps {
  isAppPath?: boolean
}

export default function TopHeader({ isAppPath = false }: TopHeaderProps) {
  const { t } = useTranslation(['common', 'articles-listing'])
  const router = useRouter()
  const currentLocale = router.query.locale || i18nextConfig.i18n.defaultLocale
  const [onboardingModal, setOnboardingModal] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const indexHref = `/${
    router.query.locale &&
    router.query.locale !== i18nextConfig.i18n.defaultLocale
      ? router.query.locale
      : ''
  }`

  const menuItems = [
    { name: 'Home', route: indexHref },
    { name: 'Articles', route: `/${currentLocale}/articles` },
    { name: 'Lists', route: `/${currentLocale}/lists` },
  ]
  const articleItems = articles
    .filter((article) => article.topNav)
    .map((article) => ({
      name: article.slug,
      route: `/${currentLocale}/${article.slug}`,
      article: true,
    }))
  const allMenuItems = menuItems.concat(articleItems)

  const handleLogoClick = (
    event: MouseEvent<HTMLAnchorElement>,
    source: 'logo' | 'heading',
  ) => {
    if (!isAppPath) return

    const newButton = document.querySelector<HTMLButtonElement>(
      '[data-test="new-button"]',
    )
    if (!newButton) return

    event.preventDefault()
    track('event', 'click-header-new-list', { event_label: source })
    newButton.click()
  }

  return (
    <>
      <div className="sticky top-0 z-50 bg-surface-card dark:bg-dark-card transition-colors border-b-[2.5px] border-ink dark:border-dark-border shadow-[0_2.5px_0_#111111] dark:shadow-[0_2.5px_0_#000000]">
        <div className="mx-auto max-w-7xl px-2 sm:px-4 lg:px-8">
          <div className="relative flex h-[54px] justify-between">
            {/* Left: logo + title */}
            <div className="relative z-10 flex items-center gap-3">
              <Link
                href={indexHref}
                prefetch={false}
                aria-label={t('Header.logoAlt')}
                onClick={(event) => handleLogoClick(event, 'logo')}
                data-test="top-header-logo"
              >
                <Logo size={30} alt={t('Header.logoAlt')} />
              </Link>
              <Link
                href={indexHref}
                prefetch={false}
                onClick={(event) => handleLogoClick(event, 'heading')}
              >
                <h1
                  data-test="top-header-heading"
                  className="font-extrabold text-base lg:text-xl text-ink dark:text-dark-ink whitespace-nowrap transition-colors"
                  style={{ letterSpacing: '-0.01em', lineHeight: 1 }}
                >
                  {t('Header.title')}
                </h1>
              </Link>
            </div>

            {/* Right: icon buttons */}
            <div className="relative z-10 flex items-center gap-1">
              <ThemeChanger />
              <IconButton
                label={t('Header.openHelp')}
                onClick={() => setOnboardingModal(true)}
                data-test="open-onboarding-modal-button"
              >
                <QuestionMarkCircleIcon
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              </IconButton>
              <IconButton
                label={t('Header.openMenu')}
                onClick={() => setMenuOpen(true)}
              >
                <Bars3Icon className="h-5 w-5" aria-hidden="true" />
              </IconButton>
            </div>
          </div>
        </div>
      </div>

      {onboardingModal && (
        <OnboardingModal handleClose={() => setOnboardingModal(false)} />
      )}

      <Drawer
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        title={t('Header.menuTitle')}
        closeLabel={t('Accessibility.closePanel')}
      >
        <nav className="flex flex-col">
          {allMenuItems.map(
            (item: { name: string; route: string; article?: boolean }) => (
              <NavItem
                key={item.route}
                href={item.route}
                onClick={() => {
                  setMenuOpen(false)
                  track('event', 'menu-click', { event_label: item.route })
                }}
              >
                {item.article
                  ? t(`articles-listing:articles.${item.name}.title`)
                  : t(`navItems.${item.name}`)}
              </NavItem>
            ),
          )}
        </nav>
        <p className="mt-auto pt-8 text-xs tracking-wider uppercase text-ink-secondary dark:text-dark-ink2 transition-colors">
          © {new Date().getFullYear()} Pros &amp; Cons List
        </p>
      </Drawer>
    </>
  )
}
